import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Card,
  Spinner,
  Button,
  Badge,
  OverlayTrigger,
  Tooltip,
  Modal,
} from "react-bootstrap";
import {
  getDocStatus,
  getListDocs,
  softDelete,
  uploadPDF,
} from "../services/document";
import "bootstrap/dist/css/bootstrap.min.css";

// Define the document type
interface Document {
  created_at: string;
  document_id: string;
  document_path: string;
  extracted_text: null;
  status: string;
  updated_at: string;
  version: number;
}

interface DocumentsTableProps {
  onSelectDocument?: (
    document_path: string,
    documentId: string,
    status: string
  ) => void;
  tableHeight?: string;
}

const DocumentsTable: React.FC<DocumentsTableProps> = ({
  onSelectDocument,
  tableHeight = "400px",
}) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);

  // Single polling interval reference
  const pollingIntervalRef = useRef<number | null>(null);
  // Flag to prevent concurrent API calls
  const isFetchingRef = useRef<boolean>(false);
  // Documents that need status updates
  const [pendingDocuments, setPendingDocuments] = useState<Map<string, number>>(
    new Map()
  );

  const VIEW_PDF_STATUS = ["UPLOADED", "PENDING", "PARSED"];
  const LOADER_STATUS = [
    "UPLOADED",
    "PENDING",
    "PARSED",
    "SCRAPING",
    "FTO_GENERATION",
  ];

  // Fetch all documents
  const fetchDocuments = async () => {
    if (isFetchingRef.current) return; // Prevent concurrent calls

    try {
      isFetchingRef.current = true;
      setError(null);
      setLoading(true);

      const response = await getListDocs();
      setDocuments(response as Document[]);

      // Update the pending documents map - track any document that isn't DONE
      const pendingDocs = new Map<string, number>();
      response.forEach((doc: Document) => {
        if (doc.status !== "DONE") {
          pendingDocs.set(doc.document_id, doc.version);
        }
      });

      setPendingDocuments(pendingDocs);
    } catch (err) {
      setError("Failed to fetch documents");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  // Check status for pending documents
  const checkPendingDocuments = async () => {
    if (isFetchingRef.current || pendingDocuments.size === 0) return;

    isFetchingRef.current = true;
    let statusChanged = false;

    try {
      // Create array of promises for all pending document status checks
      const statusChecks = Array.from(pendingDocuments.keys()).map(
        async (docId) => {
          try {
            const response = await getDocStatus(docId);

            // Update document status in state
            setDocuments((prevDocs) =>
              prevDocs.map((doc) =>
                doc.document_id === docId
                  ? { ...doc, status: response.status }
                  : doc
              )
            );

            // If status is DONE, mark for removal from pending list
            if (response.status === "DONE") {
              statusChanged = true;
              return { docId, remove: true };
            }

            return { docId, remove: false };
          } catch (err) {
            return { docId, remove: false };
          }
        }
      );

      // Wait for all status checks to complete
      const results = await Promise.all(statusChecks);

      // Update pending documents list by removing completed ones
      if (statusChanged) {
        setPendingDocuments((prevMap) => {
          const updatedMap = new Map(prevMap);
          results.forEach((result) => {
            if (result.remove) {
              updatedMap.delete(result.docId);
            }
          });
          return updatedMap;
        });
      }
    } catch (err) {
      console.error("Error during status polling:", err);
    } finally {
      isFetchingRef.current = false;
    }
  };

  // Set up and manage polling
  useEffect(() => {
    // Initial fetch
    fetchDocuments();

    // Clean up function
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  // Handle changes to pending documents
  useEffect(() => {
    // If we have pending documents but no polling interval, start polling
    if (pendingDocuments.size > 0 && !pollingIntervalRef.current) {
      pollingIntervalRef.current = window.setInterval(() => {
        checkPendingDocuments();
      }, 10 * 1000); // Poll every 30 seconds
    }
    // If no pending documents and polling is active, stop polling
    else if (pendingDocuments.size === 0 && pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, [pendingDocuments]);

  function timeout(delay: number) {
    return new Promise((res) => setTimeout(res, delay));
  }

  // Upload document function
  const uploadDocument = async (file: File) => {
    try {
      const response = await uploadPDF(file);

      // Add new document to state
      const newDoc = {
        document_id: response.file_id,
        created_at: new Date().toISOString(),
        document_path: "",
        extracted_text: null,
        status: response.process_status,
        version: 0,
        updated_at: new Date().toISOString(),
      };

      setDocuments((prev) => [...prev, newDoc]);

      setPendingDocuments((prev) => {
        const updated = new Map(prev);
        updated.set(response.file_id as string, 0);
        return updated;
      });

      return response.file_id;
    } catch (err) {
      return null;
    }
  };

  // Function to format the date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Function to get status badge variant
  const getStatusVariant = (status: string) => {
    switch (status) {
      case "DONE":
        return "success";
      case "PENDING":
        return "warning";
      case "FAILED":
        return "danger";
      default:
        return "secondary";
    }
  };

  // Function to open delete confirmation modal
  const confirmDelete = (id: string) => {
    setDocumentToDelete(id);
    setShowDeleteModal(true);
  };

  // Function to handle the actual deletion after confirmation
  const handleDelete = async () => {
    if (!documentToDelete) return;

    try {
      const response = await softDelete(documentToDelete);
      if (response.ok) {
        // Remove from pending documents if present
        setPendingDocuments((prev) => {
          const updated = new Map(prev);
          updated.delete(documentToDelete);
          return updated;
        });

        await fetchDocuments();
      }
    } catch (err) {
      setError("Failed to delete document");
    } finally {
      // Close the modal and reset the document to delete
      setShowDeleteModal(false);
      setDocumentToDelete(null);
    }
  };

  return (
    <>
      <Card className="shadow-sm border-0 mb-4">
        <Card.Header className="bg-white d-flex justify-content-between align-items-center py-3">
          <h5 className="mb-0">Uploaded Documents</h5>
          <input
            type="file"
            id="document-upload"
            className="d-none"
            accept=".pdf"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                uploadDocument(file);
                // Reset the input
                e.target.value = "";
              }
            }}
          />
          <Button
            variant="primary"
            size="sm"
            onClick={() => document.getElementById("document-upload")?.click()}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="me-1"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
            Upload New
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {loading && documents.length === 0 ? (
            <div className="text-center p-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading documents...</p>
            </div>
          ) : error ? (
            <div className="text-center p-4 text-danger">
              <p>{error}</p>
              <Button
                variant="outline-primary"
                onClick={() => fetchDocuments()}
              >
                Retry
              </Button>
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center p-4 text-muted">
              <p>No documents uploaded yet.</p>
              <Button
                variant="outline-primary"
                onClick={() =>
                  document.getElementById("document-upload")?.click()
                }
              >
                Upload your first document
              </Button>
            </div>
          ) : (
            <div
              style={{
                height: tableHeight,
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              <Table responsive hover className="mb-0">
                <thead
                  className="bg-light"
                  style={{ position: "sticky", top: 0, zIndex: 1 }}
                >
                  <tr>
                    <th className="text-center" style={{ width: "70px" }}>
                      S.No
                    </th>
                    <th className="text-center">ID</th>
                    <th>Version</th>
                    <th className="text-center" style={{ width: "120px" }}>
                      Status
                    </th>
                    <th className="text-center" style={{ width: "180px" }}>
                      Created At
                    </th>
                    <th className="text-center" style={{ width: "200px" }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc, index) => (
                    <tr key={doc.document_id}>
                      <td className="text-center">{index + 1}</td>
                      <td className="text-center">
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>{doc.document_id}</Tooltip>}
                        >
                          <span className="text-monospace">
                            {doc.document_id}
                          </span>
                        </OverlayTrigger>
                      </td>
                      <td>{doc.version}</td>
                      <td className="text-center">
                        <Badge
                          bg={getStatusVariant(doc.status)}
                          className="d-inline-flex align-items-center"
                        >
                          {LOADER_STATUS.indexOf(doc.status) >= 0 && (
                            <Spinner
                              animation="border"
                              size="sm"
                              className="me-1"
                              style={{ width: "0.5rem", height: "0.5rem" }}
                            />
                          )}
                          {doc.status}
                        </Badge>
                      </td>
                      <td className="text-center">
                        {formatDate(doc.created_at)}
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          disabled={VIEW_PDF_STATUS.indexOf(doc.status) >= 0}
                          onClick={() => {
                            onSelectDocument &&
                              onSelectDocument(
                                doc.document_path,
                                doc.document_id,
                                doc.status
                              );
                          }}
                        >
                          View
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => confirmDelete(doc.document_id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this document?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DocumentsTable;
